import React, { useState, useMemo, useRef } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Star,
  MapPin,
  Clock,
  ShoppingBag,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Store,
  TrendingUp,
  Eye,
  Edit3,
  Save,
  X,
  Package,
  Trash2,
  EyeOff,
  History,
  Loader2,
  RefreshCw,
  Sparkles,
  Camera,
} from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { auth, storage, functions } from '../../config/firebase';
import { ensureBrowserCompatibleImage, resizeImageFile, IMAGE_LIMITS, padImageToAspectRatio } from '../../lib/imageUtils';
import { getFeaturedHeroImage } from '../../lib/featuredHero';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { formatTime12h } from '../../utils/time';
import {
  useAdminBoutiques,
  useAdminLogs,
  AdminBoutique,
  BoutiqueUpdate,
  updateBoutique,
  toggleFeatured,
  updateFeaturedOrder,
  updateFeaturedImage,
  setBoutiqueStatus,
  deleteBoutique,
} from '../../hooks/useAdminBoutiques';
import {
  useAdminProducts,
  AdminProduct,
  toggleProductActive,
  deleteProduct,
} from '../../hooks/useAdminProducts';

// ─── Status chip config ──────────────────────────────────────────
const statusConfig: Record<string, { bg: string; text: string; icon: React.ElementType; label: string }> = {
  active:    { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle,   label: 'Active' },
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock,         label: 'Pending' },
  suspended: { bg: 'bg-red-100',   text: 'text-red-700',   icon: XCircle,       label: 'Suspended' },
};

function getStatus(b: AdminBoutique): 'active' | 'pending' | 'suspended' {
  if (!b.active && b.createdAt) return 'suspended';
  if (!b.active) return 'pending';
  return 'active';
}

// ─── Address helper ──────────────────────────────────────────────
function formatAddress(addr: string | { street: string; city: string; state: string; zipCode: string; unit?: string } | undefined | null): string {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return [addr.street, addr.unit, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');
}

// ─── Days helper ─────────────────────────────────────────────────
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

// ─── PRODUCT MANAGEMENT PANEL ────────────────────────────────────
function ProductsPanel({ boutique }: { boutique: AdminBoutique }) {
  const { products, loading, error } = useAdminProducts(boutique.id);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const handleToggle = async (product: AdminProduct) => {
    setActionLoading(product.id);
    try {
      await toggleProductActive(product);
    } catch (err: any) {
      alert('Failed to toggle product: ' + err.message);
    }
    setActionLoading(null);
  };

  const handleDelete = async (product: AdminProduct) => {
    setActionLoading(product.id);
    try {
      await deleteProduct(product);
      setConfirmDelete(null);
    } catch (err: any) {
      alert('Failed to delete product: ' + err.message);
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-body">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading products...
      </div>
    );
  }

  if (error) {
    return <p className="text-red-500 text-sm py-4">Error loading products: {error}</p>;
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-body">
        <Package className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm">No products found for this boutique</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-body mb-2">{products.length} product{products.length !== 1 ? 's' : ''}</p>
      {products.map((p) => (
        <div
          key={p.id}
          className={cn(
            'flex items-center gap-3 p-3 rounded-xl border transition-colors',
            p.active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-70'
          )}
        >
          {/* Thumbnail */}
          {p.images?.[0] ? (
            <img src={p.images[0]} alt={p.name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
              <Package className="h-5 w-5 text-gray-400" />
            </div>
          )}

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-heading truncate">{p.name}</p>
            <p className="text-xs text-body">${p.price?.toFixed(2)} · {p.category || 'No category'}</p>
          </div>

          {/* Status + Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={cn('text-[10px] px-2 py-0.5 rounded-full font-medium',
              p.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
            )}>
              {p.active ? 'Visible' : 'Hidden'}
            </span>

            <button
              onClick={() => handleToggle(p)}
              disabled={actionLoading === p.id}
              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
              title={p.active ? 'Hide product' : 'Show product'}
            >
              {actionLoading === p.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : p.active ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-green-600" />
              )}
            </button>

            {confirmDelete === p.id ? (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleDelete(p)}
                  disabled={actionLoading === p.id}
                  className="text-xs bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 disabled:opacity-50"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDelete(p.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                title="Delete product"
              >
                <Trash2 className="h-4 w-4 text-red-400" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── AUDIT LOG PANEL ─────────────────────────────────────────────
function AuditLogPanel({ boutiqueId }: { boutiqueId: string }) {
  const { logs, loading } = useAdminLogs(boutiqueId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-body">
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        Loading logs...
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-6 text-body">
        <History className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm">No admin actions recorded yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto">
      {logs.map((log) => (
        <div key={log.id} className="p-3 bg-gray-50 rounded-lg text-sm">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-heading capitalize">{log.action.replace(/_/g, ' ')}</span>
            <span className="text-xs text-body">
              {log.createdAt?.seconds
                ? new Date(log.createdAt.seconds * 1000).toLocaleString()
                : '—'}
            </span>
          </div>
          {log.adminNote && <p className="text-xs text-body italic mb-1">Note: {log.adminNote}</p>}
          {Object.keys(log.changes || {}).length > 0 && (
            <div className="text-xs text-body space-y-0.5">
              {Object.entries(log.changes).map(([key, val]) => (
                <div key={key}>
                  <span className="font-medium">{key}</span>:{' '}
                  <span className="text-red-500 line-through">{JSON.stringify(val.from)}</span>{' '}
                  → <span className="text-green-600">{JSON.stringify(val.to)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── BUSINESS DETAIL / EDIT PANEL (MODAL) ────────────────────────
interface DetailPanelProps {
  boutique: AdminBoutique;
  onClose: () => void;
}

function BusinessDetailPanel({ boutique, onClose }: DetailPanelProps) {
  const status = getStatus(boutique);
  const statusStyle = statusConfig[status];
  const StatusIcon = statusStyle.icon;

  // Tabs: Overview, Edit, Products, Logs
  const [tab, setTab] = useState<'overview' | 'edit' | 'products' | 'logs'>('overview');
  const [saving, setSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionReason, setActionReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteOwnerAccount, setDeleteOwnerAccount] = useState(true);
  const [deleteTypedName, setDeleteTypedName] = useState('');

  // Edit form state — seeded from boutique
  const [editName, setEditName] = useState(boutique.name);
  const [editDescription, setEditDescription] = useState(boutique.description);
  const [editAddress, setEditAddress] = useState(formatAddress(boutique.address));
  const [editDeliveryRadius, setEditDeliveryRadius] = useState(boutique.deliveryRadius ?? 5);
  const [editCommission, setEditCommission] = useState(boutique.commissionOverride ?? '');
  const [editAdminNotes, setEditAdminNotes] = useState(boutique.adminNotes ?? '');
  const [editCategories, setEditCategories] = useState(boutique.categories?.join(', ') ?? '');
  const [editHours, setEditHours] = useState<Record<string, { open: string; close: string } | null>>(
    boutique.hours ?? {}
  );

  // Social media
  const [editInstagram, setEditInstagram] = useState(boutique.socialMedia?.instagram ?? '');
  const [editTiktok, setEditTiktok] = useState(boutique.socialMedia?.tiktok ?? '');
  const [editWebsite, setEditWebsite] = useState(boutique.socialMedia?.website ?? '');
  const [editFeaturedOrder, setEditFeaturedOrder] = useState(
    boutique.featuredOrder != null ? String(boutique.featuredOrder) : ''
  );
  const [featuredImagePreview, setFeaturedImagePreview] = useState(boutique.featuredImage || '');
  const [featuredImageFile, setFeaturedImageFile] = useState<File | null>(null);
  const featuredImageInputRef = useRef<HTMLInputElement>(null);

  const heroPreview = getFeaturedHeroImage({
    featuredImage: featuredImagePreview || boutique.featuredImage,
    coverImage: boutique.coverImage,
    logo: boutique.logo,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates: BoutiqueUpdate = {
        name: editName,
        description: editDescription,
        address: editAddress,
        deliveryRadius: editDeliveryRadius,
        categories: editCategories.split(',').map((c) => c.trim()).filter(Boolean),
        hours: editHours,
        adminNotes: editAdminNotes,
        socialMedia: {
          instagram: editInstagram || undefined,
          tiktok: editTiktok || undefined,
          website: editWebsite || undefined,
        },
      };

      // Only set commission override if a value was entered
      if (editCommission !== '' && editCommission !== undefined) {
        updates.commissionOverride = Number(editCommission);
      }

      await updateBoutique(boutique, updates, 'Admin edit');
      setTab('overview');
    } catch (err: any) {
      alert('Failed to save: ' + err.message);
    }
    setSaving(false);
  };

  const handleToggleFeatured = async () => {
    setActionLoading(true);
    try {
      await toggleFeatured(boutique);
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
    setActionLoading(false);
  };

  const handleFeaturedImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0];
    if (!raw || raw.size > 10 * 1024 * 1024) return;
    try {
      const file = await ensureBrowserCompatibleImage(raw);
      const processed = await padImageToAspectRatio(file, IMAGE_LIMITS.cover.ratio, IMAGE_LIMITS.cover);
      setFeaturedImageFile(processed.file);
      const reader = new FileReader();
      reader.onloadend = () => setFeaturedImagePreview(reader.result as string);
      reader.readAsDataURL(processed.file);
    } catch {
      alert('Could not process image. Please use JPEG or PNG.');
    }
    e.target.value = '';
  };

  const handleSaveFeaturedSettings = async () => {
    setSaving(true);
    try {
      if (boutique.featured && editFeaturedOrder !== '') {
        const order = Number(editFeaturedOrder);
        if (!Number.isFinite(order) || order < 0) {
          alert('Featured order must be a non-negative number.');
          setSaving(false);
          return;
        }
        if (order !== boutique.featuredOrder) {
          await updateFeaturedOrder(boutique, order, 'Admin updated carousel order');
        }
      }

      if (featuredImageFile) {
        const sync = httpsCallable(functions, 'syncAdminClaims');
        const syncResult = await sync({});
        if ((syncResult.data as { changed?: boolean })?.changed) {
          await auth.currentUser?.getIdToken(true);
        }

        const compatible = await ensureBrowserCompatibleImage(featuredImageFile);
        const resized = await resizeImageFile(
          compatible,
          IMAGE_LIMITS.cover.maxWidth,
          IMAGE_LIMITS.cover.maxHeight,
        );
        const storageRef = ref(storage, `business-logos/${boutique.id}/featured-hero-admin`);
        const snapshot = await uploadBytes(storageRef, resized);
        const url = await getDownloadURL(snapshot.ref);
        await updateFeaturedImage(boutique, url, 'Admin uploaded homepage hero override');
        setFeaturedImageFile(null);
        setFeaturedImagePreview(url);
      }
    } catch (err: any) {
      alert('Failed to save featured settings: ' + err.message);
    }
    setSaving(false);
  };

  const handleClearFeaturedImage = async () => {
    setActionLoading(true);
    try {
      await updateFeaturedImage(boutique, null, 'Admin cleared homepage hero override');
      setFeaturedImagePreview('');
      setFeaturedImageFile(null);
    } catch (err: any) {
      alert('Failed to clear featured image: ' + err.message);
    }
    setActionLoading(false);
  };

  const handleStatusChange = async (newActive: boolean) => {
    if (!newActive && !actionReason.trim()) {
      alert('Please enter a reason for suspending this boutique.');
      return;
    }
    setActionLoading(true);
    try {
      await setBoutiqueStatus(boutique, newActive, actionReason || undefined);
      setActionReason('');
    } catch (err: any) {
      alert('Failed: ' + err.message);
    }
    setActionLoading(false);
  };

  const handleDelete = async () => {
    if (deleteTypedName !== boutique.name) {
      alert('Business name does not match. Please type the exact name to confirm.');
      return;
    }
    setActionLoading(true);
    try {
      await deleteBoutique(boutique, deleteReason || undefined, deleteOwnerAccount);
      onClose();
    } catch (err: any) {
      alert('Failed to delete business: ' + err.message);
    }
    setActionLoading(false);
  };

  const updateDayHours = (day: string, field: 'open' | 'close', value: string) => {
    setEditHours((prev) => ({
      ...prev,
      [day]: { open: prev[day]?.open ?? '10:00', close: prev[day]?.close ?? '20:00', [field]: value },
    }));
  };

  const toggleDayClosed = (day: string) => {
    setEditHours((prev) => ({
      ...prev,
      [day]: prev[day] ? null : { open: '10:00', close: '20:00' },
    }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with cover image */}
        <div className="h-40 relative flex-shrink-0">
          {boutique.coverImage ? (
            <img src={boutique.coverImage} alt={boutique.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-purple-500 to-indigo-500" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
            <div className="flex items-center gap-3">
              {boutique.logo && (
                <img src={boutique.logo} alt="" className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              )}
              <div>
                <h2 className="text-xl font-serif text-white">{boutique.name}</h2>
                <p className="text-white/80 text-sm">{formatAddress(boutique.address)}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30">
              <X className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-gray-200 px-4 flex-shrink-0">
          {([
            { id: 'overview', label: 'Overview' },
            { id: 'edit', label: 'Edit & Override' },
            { id: 'products', label: 'Products' },
            { id: 'logs', label: 'Audit Log' },
          ] as const).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                'px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                tab === t.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-body hover:text-heading'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* ── OVERVIEW TAB ────────────────────────────────── */}
          {tab === 'overview' && (
            <>
              {/* Status & Featured */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={cn('px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1', statusStyle.bg, statusStyle.text)}>
                    <StatusIcon className="h-4 w-4" />
                    {statusStyle.label}
                  </span>
                  {boutique.featured && (
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-700 flex items-center gap-1">
                      <Star className="h-4 w-4 fill-current" />
                      Featured
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 text-amber-500 fill-current" />
                  <span className="font-semibold text-heading">{boutique.rating ?? '—'}</span>
                  <span className="text-body text-sm">({boutique.reviewCount ?? 0})</span>
                </div>
              </div>

              {/* Description */}
              <p className="text-body">{boutique.description}</p>

              {boutique.featured && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-600" />
                    <h4 className="text-sm font-medium text-heading">Homepage Featured Hero</h4>
                  </div>
                  {heroPreview ? (
                    <img
                      src={heroPreview}
                      alt={`${boutique.name} homepage hero`}
                      className="w-full h-32 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 rounded-lg bg-gray-200 flex items-center justify-center text-sm text-body">
                      No hero image yet
                    </div>
                  )}
                  <div className="flex flex-wrap gap-3 text-xs text-body">
                    <span>
                      Carousel order:{' '}
                      <strong className="text-heading">
                        {boutique.featuredOrder != null ? boutique.featuredOrder : 'Not set'}
                      </strong>
                    </span>
                    <span>
                      Hero source:{' '}
                      <strong className="text-heading">
                        {boutique.featuredImage ? 'Dedicated featured image' : 'Cover / logo fallback'}
                      </strong>
                    </span>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setTab('edit')}>
                    Manage Featured Hero
                  </Button>
                </div>
              )}

              {/* Categories */}
              {boutique.categories?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {boutique.categories.map((cat) => (
                    <span key={cat} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">{cat}</span>
                  ))}
                </div>
              )}

              {/* Key details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <MapPin className="h-5 w-5 text-body flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-body">Delivery Radius</p>
                    <p className="font-medium text-heading">{boutique.deliveryRadius ?? '—'} miles</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                  <DollarSign className="h-5 w-5 text-body flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-body">Commission Override</p>
                    <p className="font-medium text-heading">
                      {boutique.commissionOverride != null ? `${boutique.commissionOverride}%` : 'Platform default'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Hours */}
              {boutique.hours && (
                <div>
                  <h4 className="text-sm font-medium text-heading mb-2 flex items-center gap-2">
                    <Clock className="h-4 w-4" /> Operating Hours
                  </h4>
                  <div className="grid grid-cols-2 gap-1.5">
                    {DAYS.map((day) => {
                      const h = boutique.hours[day];
                      return (
                        <div key={day} className="flex justify-between text-sm px-3 py-1.5 bg-gray-50 rounded-lg">
                          <span className="capitalize text-heading">{day}</span>
                          <span className="text-body">{h ? `${formatTime12h(h.open)} – ${formatTime12h(h.close)}` : 'Closed'}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Admin notes */}
              {boutique.adminNotes && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-xs font-medium text-yellow-700 mb-1">Admin Notes</p>
                  <p className="text-sm text-yellow-800">{boutique.adminNotes}</p>
                </div>
              )}

              {/* Quick actions */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                <Button variant="outline" onClick={onClose}>
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={handleToggleFeatured}
                  disabled={actionLoading}
                  className={boutique.featured ? 'text-amber-600 border-amber-300' : ''}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Star className={cn('h-4 w-4 mr-2', boutique.featured && 'fill-current')} />}
                  {boutique.featured ? 'Remove Featured' : 'Make Featured'}
                </Button>

                {status === 'active' && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Reason for suspension..."
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      className="h-9 text-sm w-48"
                    />
                    <Button
                      onClick={() => handleStatusChange(false)}
                      disabled={actionLoading}
                      className="bg-red-500 hover:bg-red-600 h-9"
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                      Suspend
                    </Button>
                  </div>
                )}

                {(status === 'suspended' || status === 'pending') && (
                  <Button
                    onClick={() => handleStatusChange(true)}
                    disabled={actionLoading}
                    className="bg-green-500 hover:bg-green-600"
                  >
                    {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    {status === 'pending' ? 'Approve' : 'Reactivate'}
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={actionLoading}
                  className="text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Business
                </Button>
              </div>

              {showDeleteConfirm && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl space-y-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Permanently delete this business?</p>
                      <p className="text-xs text-red-600 mt-1">
                        This will permanently remove the business, all its products, and optionally the owner's account. This action cannot be undone.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-red-800 mb-1">Reason for deletion</label>
                    <Input
                      placeholder="e.g. Fraudulent business, policy violation..."
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      className="h-9 text-sm border-red-200 focus:ring-red-300"
                    />
                  </div>

                  <label className="flex items-center gap-2 text-sm text-red-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={deleteOwnerAccount}
                      onChange={(e) => setDeleteOwnerAccount(e.target.checked)}
                      className="rounded border-red-300 text-red-600 focus:ring-red-500"
                    />
                    Also delete owner's Firebase account and user profile
                  </label>

                  <div>
                    <label className="block text-xs font-medium text-red-800 mb-1">
                      Type <span className="font-bold">"{boutique.name}"</span> to confirm
                    </label>
                    <Input
                      placeholder={boutique.name}
                      value={deleteTypedName}
                      onChange={(e) => setDeleteTypedName(e.target.value)}
                      className="h-9 text-sm border-red-200 focus:ring-red-300"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      onClick={handleDelete}
                      disabled={actionLoading || deleteTypedName !== boutique.name}
                      className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                      Permanently Delete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteReason('');
                        setDeleteTypedName('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── EDIT TAB ────────────────────────────────────── */}
          {tab === 'edit' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Name */}
                <div>
                  <label className="block text-xs font-medium text-heading mb-1">Name</label>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-9 text-sm" />
                </div>
                {/* Address */}
                <div>
                  <label className="block text-xs font-medium text-heading mb-1">Address</label>
                  <Input value={editAddress} onChange={(e) => setEditAddress(e.target.value)} className="h-9 text-sm" />
                </div>
                {/* Delivery Radius */}
                <div>
                  <label className="block text-xs font-medium text-heading mb-1">Delivery Radius (miles)</label>
                  <Input
                    type="number"
                    min={0}
                    value={editDeliveryRadius}
                    onChange={(e) => setEditDeliveryRadius(Number(e.target.value))}
                    className="h-9 text-sm"
                  />
                </div>
                {/* Commission Override */}
                <div>
                  <label className="block text-xs font-medium text-heading mb-1">Commission Override (%)</label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="Leave blank for platform default"
                    value={editCommission}
                    onChange={(e) => setEditCommission(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
                {/* Categories */}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-medium text-heading mb-1">Categories (comma-separated)</label>
                  <Input value={editCategories} onChange={(e) => setEditCategories(e.target.value)} className="h-9 text-sm" />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-heading mb-1">Description</label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={3}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {boutique.featured && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-heading flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-600" />
                      Homepage Featured Carousel
                    </h4>
                    <p className="text-xs text-body mt-1">
                      Control rotation order and override the hero image shown on the shopper homepage.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-heading mb-1">Carousel Order</label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0 = first in rotation"
                      value={editFeaturedOrder}
                      onChange={(e) => setEditFeaturedOrder(e.target.value)}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-heading mb-2">Featured Hero Image Override</label>
                    <div
                      onClick={() => featuredImageInputRef.current?.click()}
                      className={cn(
                        'relative w-full h-36 rounded-xl border-2 border-dashed cursor-pointer overflow-hidden',
                        featuredImagePreview || heroPreview ? 'border-transparent' : 'border-amber-200 bg-white'
                      )}
                    >
                      {(featuredImagePreview || heroPreview) ? (
                        <>
                          <img
                            src={featuredImagePreview || heroPreview}
                            alt="Featured hero preview"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/20 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                            <span className="text-white text-sm font-medium bg-black/40 px-3 py-1.5 rounded-full flex items-center gap-2">
                              <Camera className="h-4 w-4" />
                              Replace Hero Image
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full text-sm text-body">
                          <Camera className="h-7 w-7 text-amber-300 mb-2" />
                          Upload hero override
                        </div>
                      )}
                    </div>
                    <input
                      ref={featuredImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFeaturedImageSelect}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSaveFeaturedSettings}
                      disabled={saving || actionLoading}
                    >
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Featured Settings
                    </Button>
                    {(boutique.featuredImage || featuredImagePreview) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearFeaturedImage}
                        disabled={saving || actionLoading}
                        className="text-amber-700 border-amber-200"
                      >
                        Clear Hero Override
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Social Media */}
              <div>
                <h4 className="text-sm font-medium text-heading mb-2">Social Media</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] text-body mb-0.5">Instagram</label>
                    <Input value={editInstagram} onChange={(e) => setEditInstagram(e.target.value)} placeholder="https://..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-body mb-0.5">TikTok</label>
                    <Input value={editTiktok} onChange={(e) => setEditTiktok(e.target.value)} placeholder="https://..." className="h-8 text-xs" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-body mb-0.5">Website</label>
                    <Input value={editWebsite} onChange={(e) => setEditWebsite(e.target.value)} placeholder="https://..." className="h-8 text-xs" />
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div>
                <h4 className="text-sm font-medium text-heading mb-2">Operating Hours</h4>
                <div className="space-y-2">
                  {DAYS.map((day) => {
                    const h = editHours[day];
                    return (
                      <div key={day} className="flex items-center gap-3">
                        <span className="w-24 text-sm capitalize text-heading">{day}</span>
                        <button
                          onClick={() => toggleDayClosed(day)}
                          className={cn(
                            'text-xs px-2 py-1 rounded-full font-medium transition-colors',
                            h ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                          )}
                        >
                          {h ? 'Open' : 'Closed'}
                        </button>
                        {h && (
                          <>
                            <Input
                              type="time"
                              value={h.open}
                              onChange={(e) => updateDayHours(day, 'open', e.target.value)}
                              className="h-8 text-xs w-28"
                            />
                            <span className="text-body text-xs">to</span>
                            <Input
                              type="time"
                              value={h.close}
                              onChange={(e) => updateDayHours(day, 'close', e.target.value)}
                              className="h-8 text-xs w-28"
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="block text-xs font-medium text-heading mb-1">Admin Notes (internal only)</label>
                <textarea
                  value={editAdminNotes}
                  onChange={(e) => setEditAdminNotes(e.target.value)}
                  rows={2}
                  placeholder="Internal notes about this boutique..."
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>

              {/* Save */}
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setTab('overview')}>
                  Cancel
                </Button>
              </div>
            </>
          )}

          {/* ── PRODUCTS TAB ────────────────────────────────── */}
          {tab === 'products' && <ProductsPanel boutique={boutique} />}

          {/* ── AUDIT LOG TAB ───────────────────────────────── */}
          {tab === 'logs' && <AuditLogPanel boutiqueId={boutique.id} />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN ADMIN BUSINESSES PAGE
// ═══════════════════════════════════════════════════════════════════
export function AdminBusinesses() {
  const { boutiques, loading, error } = useAdminBoutiques();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Derive the selected boutique from live data so the modal always shows fresh values
  const selectedBoutique = useMemo(
    () => boutiques.find((b) => b.id === selectedId) ?? null,
    [boutiques, selectedId]
  );

  // Filter
  const filteredBusinesses = useMemo(() => {
    return boutiques.filter((biz) => {
      const matchesSearch =
        biz.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatAddress(biz.address).toLowerCase().includes(searchTerm.toLowerCase()) ||
        biz.categories?.some((c) => c.toLowerCase().includes(searchTerm.toLowerCase()));

      const status = getStatus(biz);
      const matchesStatus = statusFilter === 'all' || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [boutiques, searchTerm, statusFilter]);

  // Stats
  const stats = useMemo(() => {
    const total = boutiques.length;
    const active = boutiques.filter((b) => b.active).length;
    const pending = boutiques.filter((b) => !b.active && !b.createdAt).length;
    const featured = boutiques.filter((b) => b.featured).length;
    return [
      { label: 'Total Businesses', value: total, icon: Store, color: 'bg-purple-500' },
      { label: 'Active', value: active, icon: CheckCircle, color: 'bg-green-500' },
      { label: 'Inactive', value: total - active, icon: Clock, color: 'bg-amber-500' },
      { label: 'Featured', value: featured, icon: Star, color: 'bg-indigo-500' },
    ];
  }, [boutiques]);

  // Loading / error states
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-body">
        <Loader2 className="h-6 w-6 animate-spin mr-3" />
        Loading businesses from Firestore...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle className="h-10 w-10 text-red-400 mb-3" />
        <p className="text-heading font-medium">Failed to load businesses</p>
        <p className="text-body text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Businesses</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">
            Manage boutiques and partner businesses — live from Firestore
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-3 sm:p-4 flex items-center space-x-3 sm:space-x-4">
              <div className={cn('w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white flex-shrink-0', stat.color)}>
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-body truncate">{stat.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-heading">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search businesses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 text-sm h-9 sm:h-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 h-9 sm:h-10 text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform', showFilters && 'rotate-180')} />
            </Button>
          </div>

          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
              <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Status</label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['all', 'active', 'suspended'].map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={cn(
                      'px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize',
                      statusFilter === s ? 'bg-primary text-white' : 'bg-gray-100 text-body hover:bg-gray-200'
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Businesses Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {filteredBusinesses.map((biz) => {
          const status = getStatus(biz);
          const sStyle = statusConfig[status];
          const SIcon = sStyle.icon;

          return (
            <Card
              key={biz.id}
              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedId(biz.id)}
            >
              <div className="h-32 sm:h-40 relative">
                {biz.coverImage ? (
                  <img src={biz.coverImage} alt={biz.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-r from-purple-400 to-indigo-400" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                {biz.featured && (
                  <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-amber-500 text-white px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium flex items-center space-x-1">
                    <Star className="h-2.5 w-2.5 sm:h-3 sm:w-3 fill-current" />
                    <span>Featured</span>
                  </div>
                )}
                <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
                  <h3 className="text-sm sm:text-lg font-semibold text-white truncate">{biz.name}</h3>
                  <div className="flex items-center space-x-1 sm:space-x-2 text-white/80 text-xs sm:text-sm">
                    <Star className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400 fill-current" />
                    <span>{biz.rating ?? '—'}</span>
                    <span>·</span>
                    <span>{biz.reviewCount ?? 0} reviews</span>
                  </div>
                </div>
              </div>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 mb-2 sm:mb-3">
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-body min-w-0">
                    <MapPin className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="truncate">{formatAddress(biz.address).split(',')[0] || '—'}</span>
                  </div>
                  <span className={cn('px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium flex items-center space-x-1 flex-shrink-0', sStyle.bg, sStyle.text)}>
                    <SIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    <span>{sStyle.label}</span>
                  </span>
                </div>

                <div className="flex flex-wrap gap-1 mb-3 sm:mb-4">
                  {(biz.categories ?? []).slice(0, 3).map((cat) => (
                    <span key={cat} className="px-1.5 sm:px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] sm:text-xs">
                      {cat}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-xs text-body">
                    <MapPin className="h-3 w-3" />
                    {biz.deliveryRadius ?? '—'} mi radius
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedId(biz.id);
                    }}
                  >
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Manage</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Empty state */}
      {filteredBusinesses.length === 0 && !loading && (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <Store className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-heading font-medium text-sm sm:text-base">No businesses found</p>
            <p className="text-body text-xs sm:text-sm mt-1">
              {boutiques.length === 0
                ? 'No boutiques in Firestore yet. Add one via the scripts or business sign-up flow.'
                : 'Try adjusting your search or filters.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Detail modal — fed by live Firestore data */}
      {selectedBoutique && (
        <BusinessDetailPanel
          key={selectedBoutique.id}
          boutique={selectedBoutique}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
